import { ROLE_CUSTSVC, ROLE_CUSTOMER_SERVICE_MANAGER, ROLE_SITE_ADMIN } from '../../auth/roles'

export const BRAZE_CONFIGURATION_ROLE_IDS = [
  ROLE_SITE_ADMIN,
  ROLE_CUSTSVC,
  ROLE_CUSTOMER_SERVICE_MANAGER,
]

/** Success alerts on Braze configuration tabs auto-dismiss after this duration. */
export const BRAZE_ALERT_AUTO_DISMISS_MS = 6000

/**
 * @param {import('immutable').List<number> | number[] | undefined} roleIds
 */
export function canAccessBrazeConfiguration (roleIds) {
  if (!roleIds) {
    return false
  }
  const ids = typeof roleIds.toArray === 'function' ? roleIds.toArray() : roleIds
  return BRAZE_CONFIGURATION_ROLE_IDS.some((roleId) => ids.includes(roleId))
}

export const BRAZE_FORM_GROUP_PLAN = 'Plan & billing'
export const BRAZE_FORM_GROUP_ACQUISITION = 'Acquisition & attribution'
export const BRAZE_FORM_GROUP_CART = 'Cart & prospect'
export const BRAZE_FORM_GROUP_PROFILE = 'Profile'

export const BRAZE_FORM_GROUP_ORDER = [
  BRAZE_FORM_GROUP_PLAN,
  BRAZE_FORM_GROUP_PROFILE,
  BRAZE_FORM_GROUP_ACQUISITION,
  BRAZE_FORM_GROUP_CART,
]

export const BRAZE_BOOLEAN_OPTIONS = [
  { value: 'true', label: 'true' },
  { value: 'false', label: 'false' },
]

/**
 * @typedef {Object} BrazeFormFieldDef
 * @property {string} brazeKey
 * @property {string} label
 * @property {string} group
 * @property {string} [eventDataKey]
 * @property {'text'|'number'|'boolean'|'date'|'select'} inputType
 * @property {boolean} [saveable]
 * @property {boolean} [readOnly]
 * @property {boolean} [hiddenInForm]
 * @property {string} [tooltip] BIL-3009 operator help (from attribute-field-guide)
 * @property {'text'|'combobox'|'select'} [inputVariant]
 * @property {{ value: string, label?: string, hint?: string }[]} [suggestedOptions]
 */

/** @type {BrazeFormFieldDef[]} */
export const BRAZE_FORM_FIELDS = [
  {
    brazeKey: 'external_id',
    label: 'External ID',
    group: BRAZE_FORM_GROUP_PROFILE,
    inputType: 'text',
    readOnly: true,
    hiddenInForm: true,
  },
  {
    brazeKey: 'braze_id',
    label: 'Braze ID',
    group: BRAZE_FORM_GROUP_PROFILE,
    inputType: 'text',
    readOnly: true,
    hiddenInForm: true,
  },
  {
    brazeKey: 'plan_name',
    label: 'Plan name',
    group: BRAZE_FORM_GROUP_PLAN,
    eventDataKey: 'planName',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'subscription_tier',
    label: 'Subscription tier',
    group: BRAZE_FORM_GROUP_PLAN,
    eventDataKey: 'subscriptionTier',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'user_billing_status',
    label: 'Billing status',
    group: BRAZE_FORM_GROUP_PLAN,
    eventDataKey: 'userBillingStatus',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'in_member_base',
    label: 'In member base',
    group: BRAZE_FORM_GROUP_PLAN,
    eventDataKey: 'inMemberBase',
    inputType: 'boolean',
    saveable: true,
  },
  {
    brazeKey: 'gaia_product_sku',
    label: 'Gaia product SKU',
    group: BRAZE_FORM_GROUP_PLAN,
    eventDataKey: 'gaiaProductSku',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'paid_through_date',
    label: 'Paid through date',
    group: BRAZE_FORM_GROUP_PLAN,
    eventDataKey: 'paidThroughDate',
    inputType: 'date',
    saveable: true,
  },
  {
    brazeKey: 'next_bill_amount',
    label: 'Next bill amount',
    group: BRAZE_FORM_GROUP_PLAN,
    eventDataKey: 'nextBillAmount',
    inputType: 'number',
    saveable: true,
  },
  {
    brazeKey: 'payment_provider',
    label: 'Payment provider',
    group: BRAZE_FORM_GROUP_PLAN,
    eventDataKey: 'paymentProvider',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'currency',
    label: 'Currency',
    group: BRAZE_FORM_GROUP_PLAN,
    eventDataKey: 'currency',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'trial_period',
    label: 'Trial period',
    group: BRAZE_FORM_GROUP_PLAN,
    eventDataKey: 'trialPeriod',
    inputType: 'boolean',
    saveable: true,
  },
  {
    brazeKey: 'plan_period',
    label: 'Plan period',
    group: BRAZE_FORM_GROUP_PLAN,
    eventDataKey: 'planPeriod',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'entitled',
    label: 'Entitled',
    group: BRAZE_FORM_GROUP_PLAN,
    eventDataKey: 'entitled',
    inputType: 'boolean',
    saveable: true,
  },
  {
    brazeKey: 'hold_start_date',
    label: 'Hold start date',
    group: BRAZE_FORM_GROUP_PLAN,
    eventDataKey: 'holdStartDate',
    inputType: 'date',
    saveable: true,
  },
  {
    brazeKey: 'hold_end_date',
    label: 'Hold end date',
    group: BRAZE_FORM_GROUP_PLAN,
    eventDataKey: 'holdEndDate',
    inputType: 'date',
    saveable: true,
  },
  {
    brazeKey: 'country',
    label: 'Country',
    group: BRAZE_FORM_GROUP_PROFILE,
    eventDataKey: 'country',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'registration_language',
    label: 'Registration language',
    group: BRAZE_FORM_GROUP_PROFILE,
    eventDataKey: 'userLanguage',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'creation_source',
    label: 'Creation source',
    group: BRAZE_FORM_GROUP_PROFILE,
    eventDataKey: 'creationSource',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'utm_source',
    label: 'UTM source',
    group: BRAZE_FORM_GROUP_ACQUISITION,
    eventDataKey: 'utmSource',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'utm_medium',
    label: 'UTM medium',
    group: BRAZE_FORM_GROUP_ACQUISITION,
    eventDataKey: 'utmMedium',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'utm_campaign',
    label: 'UTM campaign',
    group: BRAZE_FORM_GROUP_ACQUISITION,
    eventDataKey: 'utmCampaign',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'utm_rainbow',
    label: 'UTM rainbow',
    group: BRAZE_FORM_GROUP_ACQUISITION,
    eventDataKey: 'utmRainbow',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'utm_platcamp',
    label: 'UTM platcamp',
    group: BRAZE_FORM_GROUP_ACQUISITION,
    eventDataKey: 'utmPlatcamp',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'utm_parameters',
    label: 'UTM parameters',
    group: BRAZE_FORM_GROUP_ACQUISITION,
    eventDataKey: 'utmParameters',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'segment_acq',
    label: 'Segment acq',
    group: BRAZE_FORM_GROUP_ACQUISITION,
    eventDataKey: 'segmentAcq',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'everflow_transaction_id',
    label: 'Everflow transaction ID',
    group: BRAZE_FORM_GROUP_ACQUISITION,
    eventDataKey: 'everflowTransactionId',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'prospect_content_id',
    label: 'Prospect content ID',
    group: BRAZE_FORM_GROUP_CART,
    eventDataKey: 'prospectContentId',
    inputType: 'text',
    saveable: true,
  },
  {
    brazeKey: 'cart_start_date',
    label: 'Cart start date',
    group: BRAZE_FORM_GROUP_CART,
    eventDataKey: 'cartStartDate',
    inputType: 'date',
    saveable: true,
  },
  {
    brazeKey: 'cart_reactivation_date',
    label: 'Cart reactivation date',
    group: BRAZE_FORM_GROUP_CART,
    eventDataKey: 'cartReactivationDate',
    inputType: 'date',
    saveable: true,
  },
]

/**
 * `fields` passed to `brazeUserProfileAttributes` — one key per form row so Braze export
 * returns every attribute the UI displays (Brooklyn default export omits several of these).
 */
export const BRAZE_PROFILE_EXPORT_FIELD_KEYS = BRAZE_FORM_FIELDS.map(({ brazeKey }) => brazeKey)

/** @deprecated use BRAZE_FORM_FIELDS */
export const BRAZE_ATTRIBUTE_LABELS = BRAZE_FORM_FIELDS.reduce((acc, field) => {
  acc[field.brazeKey] = field.label
  return acc
}, {})
